"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.grantAllowance = exports.getUsdRate = exports.getAllowanceRequired = exports.getERC20Allowance = exports.normalizeAmount = void 0;
const web3_utils_1 = require("web3-utils");
const api_1 = require("../../../../api");
const bignumber_1 = require("../bignumber");
const zrxService_1 = require("../zrxService");
/**
 * Very large amounts like those found in ERC20s with a precision of 18 get converted
 * to exponential notation ('1.6e+21') in javascript. The 0x api doesn't play well with
 * exponential notation so we need to ensure that it is represented as an integer string.
 * This function keeps 17 significant digits, so even if we try to trade 1 Billion of an
 * ETH or ERC20, we still keep 7 decimal places.
 * @param amount
 */
const normalizeAmount = (amount) => {
    if (!amount)
        return;
    return (0, bignumber_1.bnOrZero)(amount).toNumber().toLocaleString('fullwide', { useGrouping: false });
};
exports.normalizeAmount = normalizeAmount;
const getERC20Allowance = async ({ erc20AllowanceAbi, web3, tokenId, ownerAddress, spenderAddress }) => {
    const erc20Contract = new web3.eth.Contract(erc20AllowanceAbi, tokenId);
    return erc20Contract.methods.allowance(ownerAddress, spenderAddress).call();
};
exports.getERC20Allowance = getERC20Allowance;
const getAllowanceRequired = async ({ receiveAddress, allowanceContract, sellAsset, sellAmount, web3, erc20AllowanceAbi }) => {
    try {
        if (sellAsset.assetId === 'eip155:1/slip44:60') {
            return (0, bignumber_1.bn)(0);
        }
        const ownerAddress = receiveAddress;
        const spenderAddress = allowanceContract;
        const tokenId = sellAsset.tokenId;
        if (!tokenId) {
            throw new api_1.SwapError('[getAllowanceRequired] - sellAsset.tokenId is required', {
                code: api_1.SwapErrorTypes.VALIDATION_FAILED
            });
        }
        const allowanceOnChain = await (0, exports.getERC20Allowance)({
            web3,
            erc20AllowanceAbi,
            ownerAddress,
            spenderAddress,
            tokenId
        });
        if (allowanceOnChain === '0')
            return (0, bignumber_1.bnOrZero)(sellAmount);
        if (!allowanceOnChain) {
            throw new api_1.SwapError(`[getAllowanceRequired] - No allowance data`, {
                details: { allowanceContract, receiveAddress },
                code: api_1.SwapErrorTypes.RESPONSE_ERROR
            });
        }
        const allowanceRequired = (0, bignumber_1.bnOrZero)(sellAmount).minus(allowanceOnChain);
        return allowanceRequired.lt(0) ? (0, bignumber_1.bn)(0) : allowanceRequired;
    }
    catch (e) {
        if (e instanceof api_1.SwapError)
            throw e;
        throw new api_1.SwapError('[getAllowanceRequired]', {
            cause: e,
            code: api_1.SwapErrorTypes.ALLOWANCE_REQUIRED_FAILED
        });
    }
};
exports.getAllowanceRequired = getAllowanceRequired;
const getUsdRate = async (input) => {
    const { symbol, tokenId } = input;
    try {
        const USDC_CONTRACT_ADDRESS = '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48';
        if ((tokenId === null || tokenId === void 0 ? void 0 : tokenId.toLowerCase()) === USDC_CONTRACT_ADDRESS)
            return '1'; // Will break if comparing against usdc
        const rateResponse = await zrxService_1.zrxService.get('/swap/v1/price', {
            params: {
                buyToken: USDC_CONTRACT_ADDRESS,
                buyAmount: '1000000000',
                sellToken: tokenId || symbol
            }
        });
        const price = (0, bignumber_1.bnOrZero)(rateResponse.data.price);
        if (!price.gt(0))
            throw new api_1.SwapError('[getUsdRate] - Failed to get price data', {
                code: api_1.SwapErrorTypes.RESPONSE_ERROR
            });
        return (0, bignumber_1.bn)(1).dividedBy(price).toString();
    }
    catch (e) {
        if (e instanceof api_1.SwapError)
            throw e;
        throw new api_1.SwapError('[getUsdRate]', {
            cause: e,
            code: api_1.SwapErrorTypes.USD_RATE_FAILED
        });
    }
};
exports.getUsdRate = getUsdRate;
const grantAllowance = async ({ quote, wallet, adapterManager, erc20Abi, web3 }) => {
    var _a, _b, _c, _d, _e;
    try {
        if (!quote.sellAsset.tokenId) {
            throw new api_1.SwapError('[grantAllowance] - sellAsset.tokenId is required', {
                code: api_1.SwapErrorTypes.VALIDATION_FAILED
            });
        }
        const adapter = await adapterManager.byChainId('eip155:1');
        const erc20Contract = new web3.eth.Contract(erc20Abi, quote.sellAsset.tokenId);
        const approveTx = erc20Contract.methods
            .approve(quote.allowanceContract, quote.sellAmount)
            .encodeABI();
        const accountNumber = (0, bignumber_1.bnOrZero)(quote.sellAssetAccountId).toNumber();
        const bip44Params = adapter.buildBIP44Params({ accountNumber });
        const { txToSign } = await adapter.buildSendTransaction({
            wallet,
            to: quote.sellAsset.tokenId,
            bip44Params,
            value: '0',
            chainSpecific: {
                erc20ContractAddress: quote.sellAsset.tokenId,
                gasPrice: (0, web3_utils_1.numberToHex)(((_b = (_a = quote.feeData) === null || _a === void 0 ? void 0 : _a.chainSpecific) === null || _b === void 0 ? void 0 : _b.gasPrice) || 0),
                gasLimit: (0, web3_utils_1.numberToHex)(((_d = (_c = quote.feeData) === null || _c === void 0 ? void 0 : _c.chainSpecific) === null || _d === void 0 ? void 0 : _d.estimatedGas) || 0)
            }
        });
        const grantAllowanceTxToSign = {
            ...txToSign,
            data: approveTx
        };
        if (wallet.supportsOfflineSigning()) {
            const signedTx = await adapter.signTransaction({ txToSign: grantAllowanceTxToSign, wallet });
            const broadcastedTxId = await adapter.broadcastTransaction(signedTx);
            return broadcastedTxId;
        }
        else if (wallet.supportsBroadcast() && adapter.signAndBroadcastTransaction) {
            const broadcastedTxId = await ((_e = adapter.signAndBroadcastTransaction) === null || _e === void 0 ? void 0 : _e.call(adapter, {
                txToSign: grantAllowanceTxToSign,
                wallet
            }));
            return broadcastedTxId;
        }
        else {
            throw new api_1.SwapError('[grantAllowance] - invalid HDWallet config', {
                code: api_1.SwapErrorTypes.SIGN_AND_BROADCAST_FAILED
            });
        }
    }
    catch (e) {
        if (e instanceof api_1.SwapError)
            throw e;
        throw new api_1.SwapError('[grantAllowance]', {
            cause: e,
            code: api_1.SwapErrorTypes.GRANT_ALLOWANCE_FAILED
        });
    }
};
exports.grantAllowance = grantAllowance;
